<script setup lang="ts">
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import StudentSearchForm from '@/components/Student/Search/StudentSearchForm.vue';
import StudentSearchResults from '@/components/Student/Search/StudentSearchResults.vue';
import type StudentSearchCriteria from '@/types/StudentSearchCriteria';
import StudentRepository from '@/repositories/StudentRepository';
import NoResultsDialog from '@/components/Student/Search/NoResultsDialog.vue';
import { printableStudentFromCourseStudent, type FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import type { EnrollmentRow } from '@/types/EnrollmentTable';
import router from '@/router';
import { useSearchResultsStore } from '@/stores/StudentSearchResults';

const props = defineProps<{
    onSearchPage: boolean;
}>();

const $q = useQuasar();
const resultsStore = useSearchResultsStore();

const loading = ref(false);
const studentRows = ref<EnrollmentRow[]>([]);

const createStudentRows = (students: FlightSchoolCourseStudent[]): EnrollmentRow[] => students.map(
    (student: FlightSchoolCourseStudent): EnrollmentRow => ({
        ...printableStudentFromCourseStudent(student),
        isFocused: false,
    }),
);

if (!props.onSearchPage) {
    if (resultsStore.students.length === 0) {
        router.push({ name: 'studentSearch' });
    } else {
        studentRows.value = createStudentRows(resultsStore.students);

        router.push({ name: 'studentSearchResults' });
    }
}

const promptNoResults = () => {
    $q.dialog({
        component: NoResultsDialog,
        componentProps: {
            title: 'No results found',
            message: 'No students matched the given criteria.  Please check your search criteria and try again.',
        },
    });
};

const getResults = async (request: StudentSearchCriteria) => {
    loading.value = true;
    const results = await StudentRepository.studentSearch(request)
        .catch((error: unknown) => {
            const message = error instanceof Error ? error.message : '';
            $q.notify({
                color: 'negative',
                icon: 'warning',
                message: `An error occured: ${message}`,
            });

            // TODO: log error
            return [];
        })
        .finally(() => { loading.value = false; });

    resultsStore.setStudents(results);

    if (results.length === 0) {
        promptNoResults();
    } else {
        studentRows.value = createStudentRows(results);
        router.push({ name: 'studentSearchResults' });
    }
};
</script>
<template>
    <h2 class="header q-pa-xl fsum-generic-header text-center">
        {{ onSearchPage ? 'Student Search' : 'Results' }}
    </h2>
    <StudentSearchForm
        v-if="onSearchPage"
        :loading="loading"
        @send:request="(value: StudentSearchCriteria) => getResults(value)"
    />
    <StudentSearchResults
        v-else
        :search-results="studentRows"
        :loading="loading"
    />
</template>
<style scoped lang="scss">
</style>
